import {incomplete, Domain, State, Restorer, alias, DEBUG, defaultInput} from '@acng/frontend-voyager';
import {replaceUrl} from '@acng/frontend-relativity/history';
import {noop, undefined} from '@acng/frontend-bounty/std/value.js';
import {URL} from '@acng/frontend-bounty/net/url.js';
import {debug} from '@acng/frontend-bounty/dom/debug.js';
import {document, documentElement} from '@acng/frontend-bounty/dom/document.js';

import {StateProvider} from '../../voyager/context/state.js';
import {inject} from './ng.js';
import {CONST, guard} from '@acng/frontend-rubicon';
import {extend} from '@acng/frontend-bounty/object.js';
import {rootScope} from '@acng/frontend-stargazer';

const appStartHistoryLength = window.history.length;

const app = new State().config(incomplete);
DEBUG: app.name = 'location';

const restore = Restorer(app);

const provide = StateProvider();

/**
 * Treat the "main" {@link State | state} of location in a special way.
 *
 * @deprecated Location states should be defined in templates. In particular,
 *   {@link Domain.globals} should not be used at all.
 */
export const main = new Domain().config(alias('main'), incomplete, defaultInput('/'));
main.globals = extend(rootScope);

if (location.pathname != '/') {
  replaceUrl(`${location.pathname}#${location.pathname}`);
}

/**
 * @param {angular.IAngularEvent} $evt
 */
const onLocationChangeStart = ($evt) => {
  inject('director').triggerUserPopups();

  if (restore(inject('$location').path(), true) == undefined) {
    DEBUG: if (app.VERBOSE) console.log('Prevent location change.');

    $evt.preventDefault();
    inject('user').guestSignup();
  }
};

const onLocationChangeSuccess = () => {
  const input = inject('$location').path();
  restore(input);
  provide(documentElement, app);

  if (input != main.currentInput) {
    replaceUrl(`/#${main.currentInput}`);
  }
};

let removeStartListener = noop;
let removeSuccessListener = noop;

const connectLocation = () => {
  const $rootScope = inject('$rootScope');

  removeStartListener = $rootScope.$on('$locationChangeStart', onLocationChangeStart);
  removeSuccessListener = $rootScope.$on('$locationChangeSuccess', onLocationChangeSuccess);

  if (restore(inject('$location').path()) == undefined) {
    if (inject('$location').path() == '/') {
      const error = new Error('V-NIL');
      DEBUG: error.message = 'Forced input defines nothing!';

      throw error;
    }

    DEBUG: console.log('Replace trash URL. angular.js will trigger another round!');
    replaceUrl(`/#/`);
  } else {
    provide(documentElement, app);
  }
};

const disconnectLocation = () => {
  removeStartListener();
  removeSuccessListener();
  app.routes = [main];
  restore(undefined, true);
  provide(documentElement, app);
};

/**
 * Assign an input to the location service.
 *
 * > A.k.a. "goto(route)"
 *
 * @param {Element} element
 * @param {string | URL} input
 *
 * @returns {void}
 */
const assign = (element, input) => {
  const {location} = document;
  const url = new URL(input, location.origin);
  const {pathname, search} = url;
  ASSERT: guard(url.origin, CONST(location.origin));
  DEBUG: if (debug(element)) console.log('assign', {pathname, search});
  location.hash = pathname + search;
};

/**
 * @param {string} [input]
 */
const goBack = (input) => {
  const {length} = history;
  const anchor = input && history.state?.anchors?.[input];
  const delta = anchor ? anchor - length - 1 : -1;

  if (length + delta >= appStartHistoryLength) {
    history.go(delta);
  } else {
    location.hash = input ?? `${main.defaultInput}`;
  }
};

export {connectLocation, disconnectLocation, assign, goBack};
